import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the holiday-call start screen. Three short
 * slides (what/who and why a human is the control, the main red flags, key
 * terms) shown in the reader while the 3D room downloads. Content only; the
 * reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. video call: familiar faces, an urgent payment, one safe next step */
/* ------------------------------------------------------------------ */
function CallTile({ x, name, off }: { x: number; name: string; off?: boolean }): JSX.Element {
  const cx = x + 52;
  return (
    <g>
      <rect x={x + 0.5} y="80.5" width="103" height="75" rx="8" fill={off ? C.code : C.panel2} stroke={C.line} />
      {off ? (
        <g>
          <circle cx={cx} cy="112" r="16" fill={C.panel} stroke={C.line} />
          <text x={cx} y="116.5" fontSize="12" fontWeight="700" fill={C.muted} textAnchor="middle">
            JR
          </text>
          <text x={cx} y="146" fontSize="9.5" fill={C.muted} textAnchor="middle">
            camera off
          </text>
        </g>
      ) : (
        <g>
          <circle cx={cx} cy="105" r="13" fill={C.soft} fillOpacity="0.22" />
          <path d={`M${x + 26} 155 v-6 a26 20 0 0 1 52 0 v6 z`} fill={C.soft} fillOpacity="0.16" />
        </g>
      )}
      <text x={x + 52} y="172" fontSize="11" fill={C.soft} textAnchor="middle">
        {name}
      </text>
    </g>
  );
}

export function CallArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: a video call from the boss" aria="A video call with three familiar colleagues, an urgent payment request, and a reminder to verify on a number you already have" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Video call
      </text>
      <rect className="sr-anim-pulse" x="290" y="38" width="54" height="22" rx="11" fill={C.red} fillOpacity="0.12" stroke={C.red} strokeOpacity="0.45" />
      <circle className="sr-anim-blink" cx="302" cy="49" r="3" fill={C.red} />
      <text x="310" y="53" fontSize="10.5" fontWeight="700" fill={C.red} letterSpacing="0.6">
        LIVE
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <CallTile x={16} name="Marc Weber" />
      <CallTile x={128} name="Sarah Lindner" />
      <CallTile x={240} name="Julian Roth" off />

      <rect x="16.5" y="184.5" width="327" height="46" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="201" fontSize="10.5" fill={C.muted}>
        Marc Weber
      </text>
      <text x="29" y="220" fontSize="12.5" fill={C.text}>
        Approve the Horizon payment now.
      </text>

      <rect x="16.5" y="243.5" width="327" height="38" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="33" cy="262.5" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="33" cy="262.5" r="1.8" fill={C.amber} />
      <text x="46" y="266.5" fontSize="12" fontWeight="600" fill={C.amber}>
        Verify on a number you already have
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. fake profile: a convincing card with details that do not add up  */
/* ------------------------------------------------------------------ */
function ProfileRow({ y, label, value, flag }: { y: number; label: string; value: string; flag?: boolean }): JSX.Element {
  return (
    <g>
      <text x="30" y={y + 4} fontSize="12" fill={C.muted}>
        {label}
      </text>
      <text x="330" y={y + 4} fontSize="12" fontWeight={flag ? 700 : 400} fill={flag ? C.amber : C.text} textAnchor="end">
        {value}
      </text>
    </g>
  );
}

export function ProfileArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label="Example: a connection request" aria="A polished professional profile with details that do not add up: no mutual contacts and an account created three months ago" height={H}>
      <circle cx="40" cy="62" r="18" fill={C.panel2} stroke={C.line} />
      <circle cx="40" cy="57" r="6" fill={C.soft} fillOpacity="0.4" />
      <path d="M28 74 a12 9 0 0 1 24 0 z" fill={C.soft} fillOpacity="0.3" />
      <text x="68" y="58" fontSize="14" fontWeight="700" fill={C.text}>
        Daniel Kovacs
      </text>
      <text x="68" y="74" fontSize="11" fill={C.muted}>
        Talent Partner
      </text>
      <rect className="sr-anim-pulse" x="276" y="48" width="68" height="26" rx="13" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <text x="310" y="65" fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        Accept
      </text>
      <line x1="16" y1="92.5" x2="344" y2="92.5" stroke={C.line} />

      <rect x="16.5" y="102.5" width="327" height="124" rx="8" fill={C.code} stroke={C.line} />
      <ProfileRow y={122} label="Connections" value="500+" />
      <line x1="30" y1="135.5" x2="330" y2="135.5" stroke={C.line} strokeOpacity="0.6" />
      <ProfileRow y={153} label="Mutual contacts" value="0" flag />
      <line x1="30" y1="166.5" x2="330" y2="166.5" stroke={C.line} strokeOpacity="0.6" />
      <ProfileRow y={184} label="Account created" value="3 months ago" flag />
      <line x1="30" y1="197.5" x2="330" y2="197.5" stroke={C.line} strokeOpacity="0.6" />
      <ProfileRow y={215} label="Recent posts" value="Polished, generic" />

      <clipPath id="hc-profile-scan">
        <rect x="17" y="103" width="326" height="123" rx="8" />
      </clipPath>
      <g clipPath="url(#hc-profile-scan)">
        <rect className="sr-anim-sweep" x="0" y="103" width="38" height="123" fill={C.amber} fillOpacity="0.14" />
      </g>

      <rect x="16.5" y="238.5" width="327" height="48" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="243" width="3" height="39" rx="1.5" fill={C.red} />
      <text x="32" y="259" fontSize="12" fontWeight="700" fill={C.red}>
        Looks real is not the same as is real
      </text>
      <text x="32" y="275" fontSize="11" fill={C.muted}>
        Do you actually know this person?
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary: grid of icon chips                                     */
/* ------------------------------------------------------------------ */
type Glyph = "deepfake" | "voice" | "profile" | "recon" | "wifi" | "urgency" | "callback";

function GlyphShape({ g }: { g: Glyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "deepfake":
      return (
        <g {...s}>
          <circle cx="0" cy="0" r="7.5" />
          <line x1="0" y1="-7.5" x2="0" y2="7.5" strokeDasharray="2 2.4" />
          <circle cx="-3" cy="-1.6" r="0.6" />
          <circle cx="3" cy="-1.6" r="0.6" />
        </g>
      );
    case "voice":
      return (
        <g {...s}>
          <line x1="-8" y1="-2" x2="-8" y2="2" />
          <line x1="-4" y1="-5" x2="-4" y2="5" />
          <line x1="0" y1="-8" x2="0" y2="8" />
          <line x1="4" y1="-4" x2="4" y2="4" />
          <line x1="8" y1="-1.5" x2="8" y2="1.5" />
        </g>
      );
    case "profile":
      return (
        <g {...s}>
          <circle cx="0" cy="-3.5" r="4" />
          <path d="M-7.5 8 a7.5 6 0 0 1 15 0 z" />
        </g>
      );
    case "recon":
      return (
        <g {...s}>
          <circle cx="-1.5" cy="-1.5" r="5.8" />
          <line x1="2.8" y1="2.8" x2="8" y2="8" />
        </g>
      );
    case "wifi":
      return (
        <g {...s}>
          <path d="M-8 -2 a11.5 11.5 0 0 1 16 0" />
          <path d="M-4.6 1.8 a6.5 6.5 0 0 1 9.2 0" />
          <circle cx="0" cy="6" r="1" />
        </g>
      );
    case "urgency":
      return (
        <g {...s}>
          <circle cx="0" cy="0" r="8" />
          <polyline points="0,-4.5 0,0 3.6,2" />
        </g>
      );
    case "callback":
      return (
        <g {...s}>
          <rect x="-4.5" y="-8" width="9" height="16" rx="2" />
          <line x1="-1.2" y1="5" x2="1.2" y2="5" />
        </g>
      );
  }
}

interface ChipSpec {
  g: Glyph;
  title: string;
  sub: string;
}

const CHIPS: ChipSpec[] = [
  { g: "deepfake", title: "Deepfake", sub: "AI-made face or video" },
  { g: "voice", title: "Voice clone", sub: "copied from clips" },
  { g: "profile", title: "Fake profile", sub: "built to earn trust" },
  { g: "recon", title: "Reconnaissance", sub: "public details" },
  { g: "wifi", title: "Rogue Wi-Fi", sub: "look-alike network" },
  { g: "urgency", title: "Urgency", sub: "act-now pressure" },
  { g: "callback", title: "Callback check", sub: "verify independently" },
];

export function TermsArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label="Terms at a glance" aria="Seven terms shown as labelled icon chips: deepfake, voice clone, fake profile, reconnaissance, rogue Wi-Fi, urgency and callback check" height={H}>
      {CHIPS.map((c, i) => {
        const last = i === CHIPS.length - 1;
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = last ? 16 : 16 + col * 172;
        const y = 38 + row * 64;
        const w = last ? 328 : 156;
        return (
          <g key={c.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width={w - 1} height="51" rx="10" fill={C.panel2} stroke={C.line} />
            <circle cx="26" cy="26" r="16" fill={C.mint} fillOpacity="0.12" />
            {last ? <circle className="sr-anim-ring" cx="26" cy="26" r="16" fill="none" stroke={C.mint} strokeWidth="1.2" /> : null}
            <g transform="translate(26 26)">
              <GlyphShape g={c.g} />
            </g>
            <text x="51" y="23" fontSize="12.5" fontWeight="700" fill={C.text}>
              {c.title}
            </text>
            <text x="51" y="39" fontSize="10.5" fill={C.muted}>
              {c.sub}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

export const HOLIDAY_CALL_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "A familiar face is not proof.",
    illustration: CallArt,
    caption: "Faces and voices can be copied. A check cannot.",
    sections: [
      {
        h: "What this is about",
        body: "Four days off, a company iPad, and a video call from the CFO asking for an urgent payment. You see how the attacker built it, then face the call yourself.",
      },
      {
        h: "Who it\u2019s for",
        body: "Finance teams, executives, and anyone who travels with work devices or approves payments.",
      },
      {
        h: "Why your judgement matters",
        body: "Faces, voices and profiles can all be faked. The one control that holds is you, confirming the request through a channel you already trust.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "How the call gets built.",
    illustration: ProfileArt,
    caption: "Nothing here needed malware. It needed trust.",
    sections: [
      {
        h: "Public Wi-Fi you did not check",
        body: "A look-alike network can watch or redirect what you do. Use mobile data or a VPN you trust.",
      },
      {
        h: "A profile that looks too good",
        body: "A long history and many connections can still be invented. Ask whether you genuinely know this person.",
      },
      {
        h: "A cloned face and voice",
        body: "AI can copy a colleague from public clips. Several familiar faces on one call do not make it real.",
      },
      {
        h: "Urgency and secrecy",
        body: "A rush, a confidential project name and a request to skip the usual process are the tell. Stop, then verify.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Deepfake", body: "AI-generated or altered video or audio that makes someone appear to say or do something they never did." },
      { h: "Voice clone", body: "A synthetic copy of a person\u2019s voice, built from a short public clip." },
      { h: "Fake profile", body: "An invented or hijacked professional identity, built to earn trust before any request is made." },
      { h: "Reconnaissance", body: "Gathering public details \u2014 roles, projects, colleagues \u2014 to make a lie believable." },
      { h: "Rogue Wi-Fi", body: "A look-alike network set up to capture or redirect your traffic." },
      { h: "Urgency", body: "Pressure to act before you think. The most common sign of payment fraud." },
      { h: "Callback check", body: "Confirming a request through a channel you already trust, such as a stored number \u2014 never contact details the requester supplies." },
    ],
  },
];
